import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "@/store";

/**
 * Root input selectors — one per slice.
 * RootState is imported from @/store (which derives it from rootReducer),
 * so there is no circular reference here.
 */
export const selectUserSlice = (state: RootState) => state.user;
export const selectCompanySlice = (state: RootState) => state.company;
export const selectEventsSlice = (state: RootState) => state.events;

// ─── User selectors ───────────────────────────────────────────────
export const selectCurrentUser = createSelector(
  selectUserSlice,
  (user) => user.currentUser,
);

export const selectIsAuthenticated = createSelector(
  selectUserSlice,
  (user) => user.isAuthenticated,
);

export const selectUserRole = createSelector(
  selectCurrentUser,
  (user) => user?.role ?? null,
);

export const selectTheme = createSelector(
  selectUserSlice,
  (user) => user.theme,
);

// ─── Company selectors ────────────────────────────────────────────
export const selectActiveCompany = createSelector(
  selectCompanySlice,
  (company) => company.activeCompany,
);

export const selectAllCompanies = createSelector(
  selectCompanySlice,
  (company) => company.companies,
);

// ─── Events selectors ─────────────────────────────────────────────
export const selectEventFilters = createSelector(
  selectEventsSlice,
  (events) => events.filters,
);

export const selectOpenEventId = createSelector(
  selectEventsSlice,
  (events) => events.openEventId,
);

export const selectSelectedEventIds = createSelector(
  selectEventsSlice,
  (events) => events.selectedEventIds,
);

/**
 * Derived: true when any non-default filter is active.
 * Useful for showing a "clear filters" button.
 */
export const selectHasActiveFilters = createSelector(
  selectEventFilters,
  (filters) =>
    filters.status !== "all" ||
    filters.type !== "all" ||
    filters.search !== "" ||
    filters.dateFrom !== null ||
    filters.dateTo !== null,
);

export const selectCurrentEvent = createSelector(
  selectEventsSlice,
  (events) => events.currentEvent,
);

export const selectPlatformEventMembers = createSelector(
  selectEventsSlice,
  (events) => events.platformEventMembers,
);

export const selectEventContents = createSelector(
  selectEventsSlice,
  (events) => events.eventContents,
);

export const selectEventLoading = createSelector(
  selectEventsSlice,
  (events) => events.isLoading,
);

export const selectEventError = createSelector(
  selectEventsSlice,
  (events) => events.error,
);
